import { motion } from "framer-motion";
import { Gift } from "lucide-react";
import {
  Phone,
  CheckCircle2,
  Zap,
  Flashlight,
  Wifi,
  ShieldCheck,
} from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { EASE } from "./Reveal";

const HERO_IMG = "/img/hero-800.webp";
const HERO_SRC_SET =
  "/img/hero-500.webp 500w, /img/hero-800.webp 800w, /img/hero-1200.webp 1200w";

const reportRows = [
  { icon: CheckCircle2, label: "Cuisine équipée — sols & murs", state: "Bon état", tone: "text-neon border-neon/30 bg-neon/10" },
  { icon: Zap, label: "Compteur électrique — garage", state: "Relevé OK", tone: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10" },
  { icon: Flashlight, label: "Cave — zone sombre", state: "Flash activé", tone: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10" },
];

const headlineLines = [
  { text: "États des lieux", accent: false },
  { text: "à Lyon et dans toute", accent: false },
  { text: "la Métropole lyonnaise", accent: true },
];

export default function HeroSection() {

  return (
    <section
      id="hero"
      data-testid="hero"
      className="relative overflow-hidden pb-20 pt-10 md:pb-28 md:pt-[88px]"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-grid opacity-60 [mask-image:radial-gradient(ellipse_75%_65%_at_50%_0%,black,transparent)]"
      />
      <motion.div
        aria-hidden
        className="absolute -top-44 left-1/2 hidden h-[560px] w-[880px] -translate-x-1/2 rounded-full bg-electric/25 blur-[140px] md:block"
      />
      <div
        aria-hidden
        className="absolute right-[-180px] top-44 hidden h-[420px] w-[420px] rounded-full bg-cyan-500/15 blur-[120px] md:block"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 md:px-8 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-neon/25 bg-neon/10 px-4 py-1.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-neon opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-neon" />
            </span>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-neon">
              Spécialiste des états des lieux — Métropole de Lyon
            </span>
          </motion.div>

          <h1 className="mt-7 max-w-[24ch] font-display text-[1.85rem] font-extrabold leading-[1.2] tracking-[-0.01em] sm:text-4xl sm:leading-[1.22] lg:text-[3.4rem] lg:leading-[1.22]">
            {headlineLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-gradient-cyan" : "text-white"}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45, ease: EASE }}
            data-testid="hero-promo-badge"
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-neon/40 bg-neon/10 px-4 py-2"
          >
            <Gift className="h-4 w-4 shrink-0 text-neon" aria-hidden />
            <span className="text-sm font-bold text-white">
              10 € offerts sur votre premier état des lieux{" "}
              <span className="font-medium text-neon">— Offre de lancement</span>
            </span>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
            className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            Vos états des lieux sans perte de temps.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-4 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            EDL Express accompagne les agences immobilières, administrateurs de biens,
            conciergeries et propriétaires bailleurs dans la réalisation de leurs états des
            lieux d'entrée et de sortie à Lyon, Villeurbanne, Bron, Vaulx-en-Velin, Vénissieux
            et dans toute la Métropole de Lyon.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <button
              data-testid="hero-cta-availability"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-electric px-7 py-3.5 text-sm font-semibold text-night transition-all duration-300 hover:shadow-[0_0_36px_rgba(34,211,238,0.45)] hover:brightness-110"
            >
              Demander une disponibilité
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <a
              href="/agences-immobilieres"
              data-testid="hero-cta-agences"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-7 py-3.5 text-sm font-semibold text-cyan-300 transition-colors duration-300 hover:bg-cyan-400/20"
            >
              Solutions pour les agences immobilières
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            data-testid="hero-advantages"
            className="mt-8 grid max-w-xl grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2"
          >
            {[
              "Prestataire indépendant spécialisé",
              "Intervention dans toute la Métropole de Lyon",
              "États des lieux numériques avec ImmoPad",
              "Photographies et observations détaillées",
              "Signature électronique",
              "Rapport transmis sous 24 h maximum, après vérifications",
              "Demande d'intervention simple et rapide",
            ].map((t) => (
              <p
                key={t}
                className="flex items-start gap-2 text-[13px] leading-snug text-slate-300"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
                {t}
              </p>
            ))}
          </motion.div>
          <a
            href="tel:+33622857687"
            data-testid="hero-phone-link"
            className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-cyan-300"
          >
            <Phone className="h-4 w-4 text-cyan-400" />
            Une urgence ? 06 22 85 76 87
          </a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 44, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, delay: 0.55, ease: EASE }}
          className="relative"
        >
          <div className="rounded-3xl border border-white/10 bg-surface/80 p-3 shadow-[0_30px_80px_rgba(0,0,0,0.55)] backdrop-blur-md">
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={HERO_IMG}
                srcSet={HERO_SRC_SET}
                sizes="(max-width: 1024px) 100vw, 45vw"
                width="1200"
                height="800"
                alt="Constat photo d'un séjour lors d'un état des lieux EDL-EXPRESS"
                className="h-60 w-full object-cover md:h-72"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-night via-night/30 to-transparent" />
              <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-night/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300 backdrop-blur">
                État des lieux — Sortie
              </span>
              <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full border border-neon/30 bg-neon/10 px-2.5 py-1 font-mono text-[10px] font-semibold text-neon backdrop-blur">
                <Wifi className="h-3 w-3" /> 5G
              </span>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    Rapport N° EDL-2584
                  </p>
                  <p className="mt-1 font-display text-lg font-bold text-white">
                    Documenté sous ImmoPad
                  </p>
                </div>
                <ShieldCheck className="h-6 w-6 text-cyan-400" />
              </div>
            </div>

            <div className="space-y-2.5 p-4">
              {reportRows.map((row, i) => (
                <motion.div
                  key={row.label}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.9 + i * 0.18, ease: EASE }}
                  className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.03] px-3.5 py-2.5"
                >
                  <span className="flex items-center gap-2.5 text-sm text-slate-200">
                    <row.icon className="h-4 w-4 shrink-0 text-cyan-400" />
                    {row.label}
                  </span>
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-0.5 font-mono text-[10px] ${row.tone}`}
                  >
                    {row.state}
                  </span>
                </motion.div>
              ))}

              <div className="pt-2">
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  <span>Export du rapport</span>
                  <span className="text-cyan-300">86 %</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-electric"
                    initial={{ width: "8%" }}
                    animate={{ width: "86%" }}
                    transition={{ duration: 1.6, delay: 1.4, ease: EASE }}
                  />
                </div>
              </div>
            </div>
          </div>
          <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Aperçu de démonstration — rapport généré sous ImmoPad
          </p>
        </motion.div>
      </div>
    </section>
  );
}
